// The repair menu at check-in: what's being fixed (iPhone, Samsung, iPad, MacBook, game
// console...), the models for each, and the repair price for a part from MobileSentrix
// (its cost plus the store's markup, $90 to $250 by cost). Shared by the server and the screens.

import type { DeviceType } from './repairs.js';

/** The tiles at the top of the check-in, in the website's order. */
export const REPAIR_MENU: { type: DeviceType; label: string; sub: string }[] = [
  { type: 'iphone', label: 'iPhone', sub: 'Every iPhone' },
  { type: 'phone', label: 'Samsung & other phones', sub: 'Galaxy, Pixel, Motorola' },
  { type: 'ipad', label: 'iPad', sub: 'iPad, Air, Pro, mini' },
  { type: 'macbook', label: 'MacBook', sub: 'Air and Pro' },
  { type: 'console', label: 'Game Console', sub: 'PlayStation, Xbox, Switch' },
  { type: 'desktop', label: 'Desktop', sub: 'Computers and monitors' },
  { type: 'car', label: 'Car Part', sub: 'Screens, radios, keys' },
];

/** Types picked from a model list (the others are typed in). */
export const MENU_TYPES_WITH_MODELS: DeviceType[] = ['iphone', 'phone', 'ipad', 'macbook', 'console'];

export interface ModelFamily {
  name: string;
  models: string[];
}
export interface ModelBrand {
  brand: string;
  families: ModelFamily[];
}

const fam = (name: string, models: string[]): ModelFamily => ({ name, models });

/**
 * Models when the website's list (WCW Hub) can't be reached. Newest first. Anything missing
 * is typed in at the counter ("Not on the list? Type the model").
 */
export const FALLBACK_MODELS: Partial<Record<DeviceType, ModelBrand[]>> = {
  iphone: [{
    brand: 'Apple',
    families: [
      fam('iPhone 17', ['iPhone 17 Pro Max', 'iPhone 17 Pro', 'iPhone Air', 'iPhone 17']),
      fam('iPhone 16', ['iPhone 16 Pro Max', 'iPhone 16 Pro', 'iPhone 16 Plus', 'iPhone 16', 'iPhone 16e']),
      fam('iPhone 15', ['iPhone 15 Pro Max', 'iPhone 15 Pro', 'iPhone 15 Plus', 'iPhone 15']),
      fam('iPhone 14', ['iPhone 14 Pro Max', 'iPhone 14 Pro', 'iPhone 14 Plus', 'iPhone 14']),
      fam('iPhone 13', ['iPhone 13 Pro Max', 'iPhone 13 Pro', 'iPhone 13', 'iPhone 13 Mini']),
      fam('iPhone 12', ['iPhone 12 Pro Max', 'iPhone 12 Pro', 'iPhone 12', 'iPhone 12 Mini']),
      fam('iPhone 11', ['iPhone 11 Pro Max', 'iPhone 11 Pro', 'iPhone 11']),
      fam('iPhone X', ['iPhone XS Max', 'iPhone XS', 'iPhone XR', 'iPhone X']),
      fam('iPhone SE and older', ['iPhone SE (2022)', 'iPhone SE (2020)', 'iPhone 8 Plus', 'iPhone 8', 'iPhone 7 Plus', 'iPhone 7']),
    ],
  }],
  phone: [
    {
      brand: 'Samsung',
      families: [
        fam('Galaxy S', ['Galaxy S26 Ultra', 'Galaxy S26+', 'Galaxy S26', 'Galaxy S25 Ultra', 'Galaxy S25+', 'Galaxy S25', 'Galaxy S25 Edge', 'Galaxy S25 FE', 'Galaxy S24 Ultra', 'Galaxy S24+', 'Galaxy S24', 'Galaxy S24 FE', 'Galaxy S23 Ultra', 'Galaxy S23+', 'Galaxy S23', 'Galaxy S23 FE', 'Galaxy S22 Ultra', 'Galaxy S22+', 'Galaxy S22', 'Galaxy S21 Ultra', 'Galaxy S21+', 'Galaxy S21', 'Galaxy S21 FE', 'Galaxy S20 Ultra', 'Galaxy S20+', 'Galaxy S20', 'Galaxy S20 FE']),
        fam('Galaxy Z', ['Galaxy Z Fold 7', 'Galaxy Z Flip 7', 'Galaxy Z Fold 6', 'Galaxy Z Flip 6', 'Galaxy Z Fold 5', 'Galaxy Z Flip 5', 'Galaxy Z Fold 4', 'Galaxy Z Flip 4']),
        fam('Galaxy Note', ['Galaxy Note 20 Ultra', 'Galaxy Note 20', 'Galaxy Note 10+', 'Galaxy Note 10']),
        fam('Galaxy A', ['Galaxy A56', 'Galaxy A55', 'Galaxy A54', 'Galaxy A36', 'Galaxy A35', 'Galaxy A26', 'Galaxy A25', 'Galaxy A16', 'Galaxy A15', 'Galaxy A14', 'Galaxy A13']),
      ],
    },
    {
      brand: 'Google',
      families: [
        fam('Pixel', ['Pixel 10 Pro XL', 'Pixel 10 Pro', 'Pixel 10', 'Pixel 9 Pro XL', 'Pixel 9 Pro', 'Pixel 9', 'Pixel 9a', 'Pixel 8 Pro', 'Pixel 8', 'Pixel 8a', 'Pixel 7 Pro', 'Pixel 7', 'Pixel 7a', 'Pixel 6 Pro', 'Pixel 6', 'Pixel 6a']),
      ],
    },
    {
      brand: 'Motorola',
      families: [
        fam('Moto', ['Moto G Stylus (2024)', 'Moto G Power (2024)', 'Moto G (2024)', 'Moto G Stylus (2023)', 'Moto G Power (2023)', 'Razr+ (2024)', 'Razr (2024)', 'Edge (2024)']),
      ],
    },
  ],
  ipad: [{
    brand: 'Apple',
    families: [
      fam('iPad Pro', ['iPad Pro 13 (M5, 2025)', 'iPad Pro 11 (M5, 2025)', 'iPad Pro 13 (M4, 2024)', 'iPad Pro 11 (M4, 2024)', 'iPad Pro 12.9 (6th Gen)', 'iPad Pro 12.9 (5th Gen)', 'iPad Pro 12.9 (4th Gen)', 'iPad Pro 12.9 (3rd Gen)', 'iPad Pro 11 (4th Gen)', 'iPad Pro 11 (3rd Gen)', 'iPad Pro 11 (2nd Gen)', 'iPad Pro 11 (1st Gen)']),
      fam('iPad Air', ['iPad Air 13 (M3, 2025)', 'iPad Air 11 (M3, 2025)', 'iPad Air 13 (M2, 2024)', 'iPad Air 11 (M2, 2024)', 'iPad Air 5', 'iPad Air 4', 'iPad Air 3']),
      fam('iPad', ['iPad 11th Gen (A16, 2025)', 'iPad 10th Gen', 'iPad 9th Gen', 'iPad 8th Gen', 'iPad 7th Gen']),
      fam('iPad mini', ['iPad mini 7', 'iPad mini 6', 'iPad mini 5']),
    ],
  }],
  macbook: [{
    brand: 'Apple',
    families: [
      fam('MacBook Air', ['MacBook Air 13 (M4, 2025)', 'MacBook Air 15 (M4, 2025)', 'MacBook Air 13 (M3, 2024)', 'MacBook Air 15 (M3, 2024)', 'MacBook Air 13 (M2, 2022)', 'MacBook Air 15 (M2, 2023)', 'MacBook Air 13 (M1, 2020)', 'MacBook Air 13 (2018-2020)']),
      fam('MacBook Pro', ['MacBook Pro 14 (M5, 2025)', 'MacBook Pro 14 (M4, 2024)', 'MacBook Pro 16 (M4, 2024)', 'MacBook Pro 14 (M3, 2023)', 'MacBook Pro 16 (M3, 2023)', 'MacBook Pro 14 (M2, 2023)', 'MacBook Pro 16 (M2, 2023)', 'MacBook Pro 14 (M1, 2021)', 'MacBook Pro 16 (M1, 2021)', 'MacBook Pro 13 (M2, 2022)', 'MacBook Pro 13 (M1, 2020)', 'MacBook Pro 13 (2016-2020)', 'MacBook Pro 15 (2016-2019)', 'MacBook Pro 16 (2019)']),
    ],
  }],
  console: [
    { brand: 'PlayStation', families: [fam('PlayStation', ['PlayStation 5 Pro', 'PlayStation 5 Slim', 'PlayStation 5', 'PlayStation 4 Pro', 'PlayStation 4 Slim', 'PlayStation 4', 'DualSense Controller', 'DualShock 4 Controller'])] },
    { brand: 'Xbox', families: [fam('Xbox', ['Xbox Series X', 'Xbox Series S', 'Xbox One X', 'Xbox One S', 'Xbox One', 'Xbox Controller'])] },
    { brand: 'Nintendo', families: [fam('Nintendo', ['Nintendo Switch 2', 'Nintendo Switch OLED', 'Nintendo Switch', 'Nintendo Switch Lite', 'Joy-Con Controller'])] },
  ],
};

// ---------------------------------------------------------------- price from the part's cost

export interface MarkupTier {
  /** The tier covers parts that cost less than this (null: everything above the last tier). */
  underCents: number | null;
  addCents: number;
}

/** Parts under $20 get $90 added, up to $250 for parts that cost $300 or more. */
export const DEFAULT_REPAIR_MARKUP = ['20 | 90', '50 | 110', '100 | 140', '150 | 170', '200 | 200', '300 | 225', 'over | 250'].join('\n');

/**
 * Reads the markup rules from Settings: one line per tier, "under $ | add $", the last one
 * "over | add $". Returns null when a line can't be read.
 */
export function parseMarkupTiers(text: string | null | undefined): MarkupTier[] | null {
  const lines = (text ?? '').split('\n').map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return null;
  const out: MarkupTier[] = [];
  for (const line of lines) {
    const [a, b] = line.split('|').map((x) => x.trim().replace(/^\$/, ''));
    if (a === undefined || b === undefined) return null;
    const add = Number(b.replace(/^\+?\$?/, ''));
    if (!Number.isFinite(add) || add < 0 || add > 2000) return null;
    let under: number | null;
    if (/^(over|more|above|\*|and up|anything else|else)$/i.test(a)) under = null;
    else {
      under = Number(a.replace(/^(under|below|<)\s*\$?/i, ''));
      if (!Number.isFinite(under) || under <= 0 || under > 100_000) return null;
    }
    out.push({ underCents: under === null ? null : Math.round(under * 100), addCents: Math.round(add * 100) });
  }
  const open = out.filter((t) => t.underCents === null);
  if (open.length > 1) return null;
  const closed = out.filter((t) => t.underCents !== null).sort((x, y) => (x.underCents ?? 0) - (y.underCents ?? 0));
  return [...closed, ...open];
}

/** The repair price for a part that costs this much: cost plus the tier's markup, up to the next whole $5. */
export function repairPriceFromCost(costCents: number, tiers: MarkupTier[] | null): number | null {
  if (!Number.isFinite(costCents) || costCents <= 0) return null;
  const list = tiers?.length ? tiers : parseMarkupTiers(DEFAULT_REPAIR_MARKUP)!;
  const tier = list.find((t) => t.underCents !== null && costCents < t.underCents) ?? list.find((t) => t.underCents === null) ?? list[list.length - 1];
  const raw = Math.round(costCents) + tier.addCents;
  return Math.ceil(raw / 500) * 500;
}

// ---------------------------------------------------------------- MobileSentrix parts for a model

/** Words of a title or model, lowercase: "S22+" is "s22 plus", "2nd Gen" is "2gen", "Fold7" is "fold 7". */
export function menuWords(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/&amp;/g, ' and ')
    .replace(/\+/g, ' plus ')
    .replace(/(\d)(st|nd|rd|th)\s*gen(eration)?\b/g, '$1gen')
    .replace(/\b(fold|flip)(\d)/g, '$1 $2')
    .replace(/\bps\s?([345])\b/g, 'playstation $1')
    .replace(/[^a-z0-9.]+/g, ' ')
    .replace(/(^|\s)\.|\.(\s|$)/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

const FIT_QUALIFIERS = new Set(['pro', 'max', 'plus', 'mini', 'ultra', 'fe', 'lite', 'edge', 'air', 'xl', 'xr', 'xs', 'neo', 'slim', 'oled']);
const IGNORE_AFTER = new Set(['5g', '4g', 'lte']);
const LINE_WORDS = ['iphone', 'galaxy', 'pixel', 'moto', 'razr', 'ipad', 'macbook', 'playstation', 'xbox', 'switch'];

/**
 * True when a supplier's part title is for this phone: the model number with exactly the same
 * "Pro / Max / Plus" words after it ("iPhone 13 Pro" isn't "iPhone 13 Pro Max", and "S22+" is
 * "S22 Plus"). Titles listing several phones ("iPhone 12 / 12 Pro") fit each of them.
 */
export function partFitsPhone(model: string, title: string): boolean {
  const mw = menuWords(model).filter((w) => !IGNORE_AFTER.has(w));
  const tw = menuWords(title);
  const line = mw.find((w) => LINE_WORDS.includes(w));
  if (line && !tw.includes(line) && !(line === 'moto' && tw.includes('motorola'))) return false;
  const numberIdx = mw.findIndex((w) => /\d/.test(w));
  if (numberIdx < 0) {
    // "iPhone Air", "Razr": every word, and the word after the last one isn't a model number.
    const last = mw[mw.length - 1];
    return mw.every((w) => tw.includes(w)) && tw.some((w, i) => w === last && !/^\d/.test(tw[i + 1] ?? ''));
  }
  const num = mw[numberIdx];
  // Words before the number other than the line ("z fold 7", "moto g 2024") must be there too.
  const before = mw.slice(0, numberIdx).filter((w) => !LINE_WORDS.includes(w) && w !== 'apple' && w !== 'samsung' && w !== 'google');
  if (!before.every((w) => tw.includes(w))) return false;
  const want = new Set(mw.slice(numberIdx + 1).filter((w) => FIT_QUALIFIERS.has(w)));
  const extra = mw.slice(numberIdx + 1).filter((w) => !FIT_QUALIFIERS.has(w));
  if (!extra.every((w) => tw.includes(w))) return false;
  return tw.some((w, i) => {
    if (w !== num) return false;
    const after = new Set<string>();
    for (let j = i + 1; j < tw.length; j++) {
      if (IGNORE_AFTER.has(tw[j])) continue;
      if (!FIT_QUALIFIERS.has(tw[j])) break;
      after.add(tw[j]);
    }
    return after.size === want.size && [...after].every((x) => want.has(x));
  });
}

/**
 * Looser fit for iPads, MacBooks and consoles, whose names vary more from site to site: every
 * word of the name outside brackets ("iPad Pro 11", "PlayStation 5 Slim"), and a generation
 * in brackets ("2nd Gen") when there is one.
 */
export function partFitsDevice(model: string, title: string): boolean {
  const main = menuWords(model.replace(/\([^)]*\)/g, ' ')).filter((w) => !IGNORE_AFTER.has(w));
  const inBrackets = [...model.matchAll(/\(([^)]*)\)/g)].flatMap((m) => menuWords(m[1]));
  const gens = inBrackets.filter((w) => /^\d+gen$/.test(w));
  // Years in brackets ("M4, 2024", "2016-2020"): the part lists at least one of them.
  // Chip names (M4) aren't in the suppliers' titles, so they don't count.
  const years = inBrackets.filter((w) => /^20\d\d$/.test(w));
  const tw = new Set(menuWords(title));
  return main.length > 0 && main.every((w) => tw.has(w)) && gens.every((g) => tw.has(g)) && (!years.length || years.some((y) => tw.has(y)));
}

export const PART_GROUPS = ['screen', 'battery', 'back', 'charging', 'camera', 'audio', 'buttons', 'other'] as const;
export type PartGroup = (typeof PART_GROUPS)[number];
export const PART_GROUP_LABELS: Record<PartGroup, string> = {
  screen: 'Screen',
  battery: 'Battery',
  back: 'Back glass & housing',
  charging: 'Charging port & ports',
  camera: 'Camera',
  audio: 'Speakers & mic',
  buttons: 'Buttons, flex & keyboard',
  other: 'Other parts',
};

/** Tools, accessories and supplies on the supplier's site that aren't a repair part. */
const NOT_A_REPAIR_PART = /\b(tempered|protector|privacy glass|screen guard|case|cases|holster|wallet|popsocket|pop socket|car mount|wall charger|power adapter|charging cable|usb cable|lightning cable|usb-c cable|cable to|earbuds|headphones?|airpods|stylus|pencil tip|tools?|kit|kits|tweezers?|screwdrivers?|spudger|pry|heat gun|adhesives?|tape|glue|stickers?|labels?|mold|stencil|tester|testers|programmer|polarizer|oca|film|cleaning|wipes|bag|bags|packaging|retail box|gift|keychain|lanyard|strap|watch band|display stand|demo|mannequin|sample|refurbishing service|repair service|lcd buyback|laminat\w*|frame press|separator|uv lamp|microscope)\b/i;

/** True for the parts a technician puts in (screens, batteries, ports...), false for tools and accessories. */
export function isRepairPart(title: string): boolean {
  return !NOT_A_REPAIR_PART.test(title);
}

/** Which part of the menu a part goes in. */
export function partGroup(title: string): PartGroup {
  const t = title.toLowerCase();
  if (/\b(battery|batteries)\b/.test(t)) return 'battery';
  if (/\b(oled|lcd|amoled|incell|in-cell|digitizer|display|screen|touch panel)\b/.test(t) && !/\b(flex|cable|connector|bracket|ic|chip|board)\b/.test(t)) return 'screen';
  if (/\b(back glass|back cover|back housing|rear glass|rear housing|housing|back panel|battery door|mid ?frame|chassis|bottom case|top case|shell)\b/.test(t)) return 'back';
  if (/\b(charging|charge port|dock|usb-?c port|lightning|hdmi|port)\b/.test(t)) return 'charging';
  if (/\b(camera|lens)\b/.test(t)) return 'camera';
  if (/\b(speaker|earpiece|loudspeaker|microphone|mic|buzzer|ringer|audio)\b/.test(t)) return 'audio';
  if (/\b(button|flex|cable|keyboard|trackpad|track pad|joystick|thumbstick|trigger|power|volume|home)\b/.test(t)) return 'buttons';
  return 'other';
}

const COLOR_WORDS = /^(black|white|silver|gold|rose gold|space gr[ae]y|graphite|sierra blue|alpine green|pacific blue|midnight green|midnight|starlight|blue|red|green|purple|deep purple|pink|yellow|coral|orange|teal|ultramarine|natural titanium|blue titanium|white titanium|black titanium|desert titanium|titanium|cosmic orange|deep blue|mist blue|sage|lavender|phantom black|phantom white|phantom silver|phantom violet|phantom gray|cream|lavender|bora purple|graphite gray|burgundy|sky blue|icy blue|navy|mint|jet black|matte black|clear|product red|\(product\) red|space black|stardust|cloud white|light blue|dark gray|gray|grey|bronze|copper|champagne|obsidian|hazel|porcelain|snow|sorta seafoam|lemongrass|aloe|bay|charcoal|chalk|sea|coral red|frost)$/i;

/** A title without "Compatible For <phone>", shipping notes or the color: "OLED Assembly (Aftermarket Plus: Soft)". */
const NOTE_BRACKETS = /\s*\([^)]*\b(ground shipping|returnable|ic transfer|final sale|hazmat)\b[^)]*\)/gi;

/** A supplier title without its shipping and return notes: "(Only Ground Shipping)", "(No IC Transfer Required)". */
export function partTitleClean(title: string): string {
  return title.replace(NOTE_BRACKETS, '').replace(/\s+/g, ' ').trim();
}

export function partShortName(title: string): string {
  let s = partTitleClean(title);
  // "OLED Assembly Compatible For iPhone 13 Pro (Aftermarket Plus: Soft)" -> keep the bits in brackets.
  const m = /^(.*?)\s+(?:compatible\s+)?for\s+[^()]+((?:\s*\([^)]*\))*)\s*$/i.exec(s);
  if (m && m[1].trim().length >= 3) s = `${m[1].trim()}${m[2] ? ` ${m[2].trim()}` : ''}`;
  s = s.replace(/\s+compatible$/i, '');
  return stripColor(s).name;
}

/** The color in the last brackets, if it's a color: ("Graphite"). */
export function stripColor(name: string): { name: string; color: string | null } {
  const m = /\s*\(([^)]*)\)\s*$/.exec(name);
  if (m && COLOR_WORDS.test(m[1].trim())) return { name: name.slice(0, m.index).trim(), color: m[1].trim() };
  return { name: name.trim(), color: null };
}

/** The part's color, when the title ends with one: "(Sierra Blue)". */
export function partColor(title: string): string | null {
  return stripColor(partTitleClean(title)).color;
}

/** The whole title (with the phone it fits) without notes or color: for a typed search, where parts for different phones show together. */
export function partFullName(title: string): string {
  return stripColor(partTitleClean(title)).name;
}

/** The same part in other colors collapses into one menu item. */
export function partVariantKey(title: string): string {
  return partShortName(title).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

export const REPAIR_WORDS: Record<PartGroup, string> = {
  screen: 'Screen replacement',
  battery: 'Battery replacement',
  back: 'Back glass / housing',
  charging: 'Charging port repair',
  camera: 'Camera repair',
  audio: 'Speaker / mic repair',
  buttons: 'Button / flex repair',
  other: 'Repair',
};

/**
 * Which part a repair on the ticket replaces, from its name ("Screen Replacement - Premium" is a
 * screen), so a part picked from the suppliers goes on the right repair. Null when it can't tell.
 */
export function repairLineGroup(description: string): PartGroup | null {
  const t = description.toLowerCase();
  if (/back ?glass|back housing|housing|rear glass|back cover/.test(t)) return 'back';
  if (/battery/.test(t)) return 'battery';
  if (/charg|port|dock|hdmi|usb|lightning/.test(t)) return 'charging';
  if (/camera|lens/.test(t)) return 'camera';
  if (/speaker|earpiece|\bmic|audio/.test(t)) return 'audio';
  if (/screen|lcd|oled|display|digitizer|glass/.test(t)) return 'screen';
  if (/button|flex|keyboard|trackpad/.test(t)) return 'buttons';
  return null;
}

/** The line on the repair ticket: "Screen replacement · OLED Assembly (Aftermarket Plus: Soft)". With `full`, the phone it fits stays in. */
export function partLineDescription(group: PartGroup, title: string, full = false): string {
  return `${REPAIR_WORDS[group]} · ${full ? partFullName(title) : partShortName(title)}`.slice(0, 120);
}

/** One part on the repair menu (the same part in other colors is one item). */
export interface MenuPart {
  key: string;
  /** "OLED Assembly (Aftermarket Plus: Soft)" */
  name: string;
  /** The supplier's full title. */
  title: string;
  /** What goes on the ticket: "Screen replacement · OLED Assembly (Aftermarket Plus: Soft)". */
  description: string;
  sku: string | null;
  url: string | null;
  image: string | null;
  in_stock: boolean | null;
  colors: string[];
  /** Our cost (MobileSentrix's price); only for people who can see costs. */
  cost_cents: number | null;
  /** The repair price: cost plus the markup, to the next $5. */
  price_cents: number;
}

export interface MenuPartGroup {
  group: PartGroup;
  label: string;
  parts: MenuPart[];
  /** Parts left out of a long list (search to narrow it down). */
  more: number;
}

export interface MenuPartsResult {
  ok: boolean;
  query: string;
  groups: MenuPartGroup[];
  error?: string;
}
